import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import {
  MapPin, Plus, Search, ChevronRight, Package,
  Edit2, Trash2, Building2, X, FolderOpen, Folder
} from 'lucide-react';
import type { Location } from '../lib/api';
import { Toolbar, ResultsCount } from '../components/DataTable';

export default function Locations() {
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const queryClient = useQueryClient();

  // Listen for FAB click event from Layout
  useEffect(() => {
    const handleOpenModal = (e: CustomEvent) => {
      if (e.detail?.type === 'location') {
        setShowAddModal(true);
      }
    };
    window.addEventListener('openAddModal', handleOpenModal as EventListener);
    return () => window.removeEventListener('openAddModal', handleOpenModal as EventListener);
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['locations'],
    queryFn: api.getLocations,
    refetchOnMount: 'always'
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    }
  });

  const filteredLocations = data?.locations.filter(
    (loc) => loc.name.toLowerCase().includes(search.toLowerCase())
  ) || [];

  const rootLocations = filteredLocations.filter((loc) => !loc.parentId);
  const childrenMap = new Map<string, Location[]>();
  filteredLocations.forEach((loc) => {
    if (loc.parentId) {
      const children = childrenMap.get(loc.parentId) || [];
      children.push(loc);
      childrenMap.set(loc.parentId, children);
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-red-200 bg-red-50">
        <div className="flex items-center gap-2 text-red-700">
          <MapPin className="w-4 h-4" />
          <span className="text-sm font-medium">Errore: {error.message}</span>
        </div>
      </div>
    );
  }

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Sei sicuro di voler eliminare "${name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Ubicazioni</h1>
          <p className="page-subtitle">{data?.locations.length || 0} ubicazioni</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn btn-primary hidden lg:flex">
          <Plus className="w-4 h-4" />
          Nuova Ubicazione
        </button>
      </div>

      {/* Search Toolbar */}
      <div className="card card-sm space-y-2">
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca ubicazioni..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input input-sm pl-8 w-full"
            />
          </div>
          {search && (
            <button onClick={() => setSearch('')} className="btn btn-ghost btn-sm flex-shrink-0">
              <X className="w-3 h-3" />
              <span className="hidden sm:inline">Reset</span>
            </button>
          )}
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500 pt-1 border-t border-gray-100">
          <ResultsCount count={filteredLocations.length} label="ubicazioni" />
        </div>
      </div>

      {/* Locations Tree */}
      {rootLocations.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Building2 className="empty-state-icon" />
            <p className="empty-state-title">Nessuna ubicazione trovata</p>
            <p className="empty-state-text">
              {search ? 'Prova a modificare la ricerca' : 'Aggiungi la tua prima ubicazione'}
            </p>
            {!search && (
              <button onClick={() => setShowAddModal(true)} className="btn btn-primary btn-sm mt-3">
                <Plus className="w-4 h-4" />
                Nuova Ubicazione
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="card card-sm">
          <div className="space-y-1">
            {rootLocations.map((location) => (
              <LocationRow
                key={location.id}
                location={location}
                children={childrenMap.get(location.id) || []}
                childrenMap={childrenMap}
                onDelete={handleDelete}
                level={0}
              />
            ))}
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AddLocationModal
          locations={data?.locations || []}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}

function LocationRow({
  location,
  children,
  childrenMap,
  onDelete,
  level
}: {
  location: Location;
  children: Location[];
  childrenMap: Map<string, Location[]>;
  onDelete: (id: string, name: string) => void;
  level: number;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = children.length > 0;
  const navigate = useNavigate();

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons
    if ((e.target as HTMLElement).closest('button')) return;
    navigate(`/locations/${location.id}`);
  };

  return (
    <div>
      <div 
        className={`flex items-center gap-2 py-2 px-2 rounded hover:bg-gray-50 cursor-pointer ${level > 0 ? 'border-l-2 border-gray-200 ml-4' : ''}`}
        style={{ paddingLeft: level > 0 ? `${level * 16 + 8}px` : undefined }}
        onClick={handleRowClick}
      >
        {/* Expand/Collapse */}
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className={`icon-btn-sm p-0.5 ${!hasChildren ? 'invisible' : ''}`}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            expanded ? <FolderOpen className="w-4 h-4 text-primary-500" /> : <Folder className="w-4 h-4 text-gray-400" />
          ) : (
            <MapPin className="w-4 h-4 text-gray-300" />
          )}
        </button>

        {/* Location Icon */}
        <MapPin className="w-4 h-4 text-primary-500 flex-shrink-0" />

        {/* Name & Description */}
        <div className="flex-1 min-w-0">
          <span className="font-medium text-gray-900 text-sm">{location.name}</span>
          {location.description && (
            <span className="text-xs text-gray-500 ml-2 hidden sm:inline">
              — {location.description}
            </span>
          )}
        </div>

        {/* Asset count */}
        <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
          <Package className="w-3 h-3" />
          <span>{location.assetCount || 0}</span>
        </div>

        {/* Actions - always visible */}
        <div className="flex items-center gap-1">
          <Link 
            to={`/locations/${location.id}`} 
            className="icon-btn icon-btn-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <Edit2 className="w-3.5 h-3.5" />
          </Link>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(location.id, location.name); }}
            className="icon-btn icon-btn-sm hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {children.map((child) => (
            <LocationRow
              key={child.id}
              location={child}
              children={childrenMap.get(child.id) || []}
              childrenMap={childrenMap}
              onDelete={onDelete}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AddLocationModal({
  locations,
  onClose
}: {
  locations: Location[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState('');

  const createMutation = useMutation({
    mutationFn: api.createLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      onClose();
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      name,
      description: description || undefined,
      parentId: parentId || undefined
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Nuova Ubicazione</h2>
          <button onClick={onClose} className="icon-btn icon-btn-sm">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div className="form-group">
              <label className="form-label">Nome *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="es. Piscina principale"
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">Descrizione</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="input"
                rows={2}
                placeholder="Descrizione opzionale..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Ubicazione Padre</label>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="input"
              >
                <option value="">Nessuna (root)</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
              <p className="form-hint">Lascia vuoto per creare un'ubicazione principale</p>
            </div>

            {createMutation.error && (
              <p className="text-red-600 text-xs bg-red-50 p-2 rounded">
                {createMutation.error.message}
              </p>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Annulla
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !name.trim()}
              className="btn btn-primary"
            >
              {createMutation.isPending ? (
                <>
                  <div className="spinner spinner-sm" />
                  Creazione...
                </>
              ) : (
                'Crea Ubicazione'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
